import { SuccessPayload } from 'components/widgets/invest-component/storage/invest-states'
import { atomsWithMutation } from 'jotai-tanstack-query'
import { subgraphLinks } from 'products'
import {
  parseTransactionResult,
  TransactionResult,
} from 'services/query-v2/deposit-redeem-v1/utils'
import { fetchGraphQLData } from 'services/query/fetchers/data-graph'
import { sanitizeAddress } from 'services/query/hooks'
import { viemPublicClients, wagmiConfig } from 'services/wallet/chains'
import { Address, zeroAddress } from 'viem'
import { SubgraphAnatomy } from '../anatomy'
import { depositV2 } from './deposit'
import { BaseConfig } from './interfaces'
import { redeemV2 } from './redeem'
import { retry } from './retry'
import { quotesQuery } from '../zero-ex-requests/quotes/quotes'
import { defaultQuoteParams, QuoteParams } from '../zero-ex-requests/quotes/model'
import { formatWeiToAmount } from 'utils'
import { DEFAULT_NATIVE_DECIMALS } from 'app-constants'
import { sendTransaction } from '@wagmi/core'

const getQuery = (
  address: string,
) => `query {indexes(where: { id: "${sanitizeAddress(address)}" }) {
  config {
    AUMDilutionPerSecond
    depositCustomCallback
    depositFeeInBP
    id
    metadata
    redemptionCustomCallback
    redemptionFeeInBP
    useCustomAUMFee
  }
  lzConfig {
    eIds
    minGas
  }
  anatomy{
    chainIdSet
    currencyIdSets {
      chainIndex
      sets
    }
  }
  assets {
    chainID
    chainIndex
    assets {
      asset
      currencyID
      decimals
      symbol
    }
  }
}}`

type LzConfig = {
  eIds: string
  minGas: string[]
}

type RawConfig = {
  indexes: {
    config: {
      AUMDilutionPerSecond: string
      depositCustomCallback: boolean
      depositFeeInBP: string
      id: string
      metadata: string
      redemptionCustomCallback: boolean
      redemptionFeeInBP: string
      useCustomAUMFee: boolean
    }
    lzConfig: LzConfig
    anatomy: {
      chainIdSet: string[]
      currencyIdSets: {
        chainIndex: string
        sets: string[]
      }[]
    }
    assets: {
      chainID: string
      chainIndex: string
      assets: {
        asset: Address
        decimals: number
        symbol: string
        currencyID: string
      }[]
    }[]
  }[]
}

type AssetsConfig = {
  [chainID: number]: {
    [address: Address]: {
      symbol: string
      decimals: number
    }
  }
}

export type ParsedConfigs = {
  config: BaseConfig
  lzConfig: LzConfig
  anatomy: SubgraphAnatomy
  assets: AssetsConfig
  reserveAsset: Address
}

export type ErrorMessage = {
  code: string
  message: string
}

export const [indexV2TransactionResponseAtom, indexV2TransactionRequestAtom] = atomsWithMutation<
  TransactionResult | undefined,
  undefined,
  SuccessPayload,
  unknown
>(() => ({
  mutationKey: ['index-v2-deposit'],
  mutationFn: async (props: SuccessPayload) => {
    console.log('index-v2-execution')

    const client = viemPublicClients[props.inputCurrency.chainId]
    const { address, symbol } =
      props.inputCurrency.type === 'index-v2' ? props.inputCurrency : props.outputCurrency
    if (symbol === 'JMX') {
      const { inputCurrency, account, outputCurrency, sellAmount, stopProcessing } = props

      const params: QuoteParams = {
        ...defaultQuoteParams,
        sellCurrency: inputCurrency,
        buyCurrency: outputCurrency,
        sellAmount: sellAmount,
        chainId: inputCurrency.chainId,
        account,
      }

      if (stopProcessing) stopProcessing()

      const quotes = await quotesQuery(params)

      const {
        to: presetTo,
        data: preData,
        buyAmount: preBuyAmount,
        sellAmount: preSellAmount,
      } = quotes

      if (inputCurrency.type === 'native') {
        const adjustedAmount = preSellAmount

        const requestedQuotes = await quotesQuery({
          ...params,
          sellAmount: formatWeiToAmount(adjustedAmount, DEFAULT_NATIVE_DECIMALS),
        })

        const { to, data, buyAmount, gas, value } = requestedQuotes
        const hash = await sendTransaction(wagmiConfig, {
          to,
          account,
          value,
          data,
          gas: gas,
        })
        return parseTransactionResult(hash, props, buyAmount)

        // return parseTransactionResult('0x00123', props, Big(123))
        // INFO: test
      }
      const hash = await sendTransaction(wagmiConfig, {
        to: presetTo,
        account,
        value: inputCurrency.address === zeroAddress ? preSellAmount : BigInt(0),
        data: preData,
      })
      return parseTransactionResult(hash, props, preBuyAmount)
      // return parseTransactionResult('0x00123', props, Big(123))
      // INFO: test
    }

    const url = subgraphLinks[symbol.toUpperCase()]

    const query = getQuery(address)
    const res = await (await fetchGraphQLData<RawConfig>(url, query)).indexes[0]
    let reserveAsset: Address = zeroAddress

    const assets: AssetsConfig = Object.fromEntries(
      res.assets.map(({ chainID, assets, chainIndex }) => [
        +chainID,
        Object.fromEntries(
          assets.map(({ asset, decimals, symbol, currencyID }) => {
            if (chainIndex === '0' && currencyID === '0') {
              reserveAsset = asset
            }
            return [asset.toLowerCase() as Address, { symbol: symbol.toUpperCase(), decimals }]
          }),
        ),
      ]),
    )

    const configs: ParsedConfigs = {
      config: {
        shared: {
          AUMDilutionPerSecond: BigInt(res.config.AUMDilutionPerSecond),
          useCustomAUMFee: res.config.useCustomAUMFee,
          metadata: res.config.metadata as Address,
        },
        depositFee: {
          BPs: +res.config.depositFeeInBP,
          useCustomCallback: res.config.depositCustomCallback,
        },
        redemptionFee: {
          BPs: +res.config.redemptionFeeInBP,
          useCustomCallback: res.config.redemptionCustomCallback,
        },
      },
      lzConfig: res.lzConfig,
      anatomy: {
        chainIdSet: res.anatomy?.chainIdSet ?? 1n,
        currencyIdSets: res.anatomy
          ? res.anatomy.currencyIdSets
              .sort((a, b) => (+a.chainIndex > +b.chainIndex ? 1 : -1))
              .map((el) => el.sets)
          : [['1']],
      },
      reserveAsset,
      assets,
    }

    if (props.isRetry) {
      return await retry(client, configs, props)
    }

    if (props.inputCurrency.type === 'native' || props.inputCurrency.type === 'token') {
      return await depositV2(client, configs, props)
    }
    return await redeemV2(client, configs, props)
  },
}))
